import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api, { trackEvent, formatApiErrorDetail } from "@/lib/api";

export default function ApplyModal({ job, open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", experience: "", cover_note: "" });
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let resume_file_id = null;
      if (resume) {
        const fd = new FormData();
        fd.append("file", resume);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        resume_file_id = up.data.file_id;
      }
      await api.post("/applications", { job_id: job.slug, ...form, resume_file_id });
      trackEvent("job_application", { job: job.slug });
      toast.success("Application submitted! Our team will contact you.");
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", location: "", experience: "", cover_note: "" });
      setResume(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to apply");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="apply-modal">
        <DialogHeader><DialogTitle className="font-display">Apply for {job?.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3 mt-2">
          <div><Label>Full Name *</Label><Input data-testid="apply-name" required value={form.name} onChange={upd("name")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email *</Label><Input data-testid="apply-email" type="email" required value={form.email} onChange={upd("email")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>Phone *</Label><Input data-testid="apply-phone" required value={form.phone} onChange={upd("phone")} className="bg-white/5 border-white/10 mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Location</Label><Input data-testid="apply-location" value={form.location} onChange={upd("location")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>Experience</Label><Input data-testid="apply-experience" value={form.experience} onChange={upd("experience")} placeholder="e.g. 2 years" className="bg-white/5 border-white/10 mt-1" /></div>
          </div>
          <div><Label>Cover Note</Label><Textarea data-testid="apply-note" value={form.cover_note} onChange={upd("cover_note")} className="bg-white/5 border-white/10 mt-1" rows={2} /></div>
          <div>
            <Label>Resume (PDF/DOC, max 8MB)</Label>
            <label className="mt-1 flex items-center gap-2 px-4 py-3 rounded-lg border border-dashed border-white/20 cursor-pointer hover:border-brand-emerald bg-white/5">
              <Upload size={18} className="text-brand-emerald" />
              <span className="text-sm text-slate-400">{resume ? resume.name : "Upload your resume"}</span>
              <input data-testid="apply-resume" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files[0])} />
            </label>
          </div>
          <Button data-testid="apply-submit" type="submit" disabled={loading} className="btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold mt-2">
            {loading ? "Submitting..." : "Submit Application"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
